import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  LOGO_URL,
  NAV_LINKS,
  useStickyNav,
  navigateToHomeSection,
} from "@/lib/shared";

const linkKey = (l) => l.to || `#${l.hash}`;
const linkTestSlug = (l) => (l.to ? l.to.replace(/^\//, "") : l.hash);

const Nav = ({ showBackToHome = false }) => {
  const scrolled = useStickyNav();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const close = () => setOpen(false);
      window.addEventListener("resize", close);
      return () => window.removeEventListener("resize", close);
    }
    return undefined;
  }, [open]);

  const renderLink = (l, prefix = "nav-link") => {
    const testId = `${prefix}-${linkTestSlug(l)}`;
    if (l.to) {
      // dedicated route — full React Router navigation
      return (
        <Link
          key={linkKey(l)}
          to={l.to}
          onClick={() => setOpen(false)}
          data-testid={testId}
        >
          {l.label}
        </Link>
      );
    }
    // homepage anchor — smooth scroll if already on /, else navigate to /#hash
    return (
      <a
        key={linkKey(l)}
        href={`/#${l.hash}`}
        onClick={(e) => {
          navigateToHomeSection(e, l.hash);
          setOpen(false);
        }}
        data-testid={testId}
      >
        {l.label}
      </a>
    );
  };

  return (
    <header
      className={`cn-nav ${scrolled ? "scrolled" : ""}`}
      data-testid="nav-header"
    >
      <div className="cn-nav-inner">
        <div className="cn-nav-left">
          {showBackToHome && (
            <Link
              to="/"
              className="cn-nav-back"
              data-testid="nav-back-home"
              aria-label="Volver a inicio"
            >
              <ArrowLeft size={16} strokeWidth={2.2} />
              <span>Volver a Inicio</span>
            </Link>
          )}
          <Link
            to="/"
            className="cn-nav-logo"
            aria-label="Caruna"
            data-testid="nav-logo-link"
          >
            <img src={LOGO_URL} alt="Caruna" loading="eager" />
          </Link>
        </div>
        <nav className="cn-nav-links" aria-label="Navegación principal">
          {NAV_LINKS.map((l) => renderLink(l))}
        </nav>
        <a
          href="/#contacto"
          onClick={(e) => {
            navigateToHomeSection(e, "contacto");
            setOpen(false);
          }}
          className="cn-btn cn-btn-gradient cn-btn-sm cn-nav-cta"
          data-testid="nav-cta-demo"
        >
          Solicitar Evaluación Técnica
        </a>
        <button
          type="button"
          className="cn-nav-burger"
          aria-label="Abrir menú"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-burger"
        >
          <span />
          <span />
          <span />
        </button>
      </div>
      {open && (
        <div className="cn-nav-mobile" data-testid="nav-mobile-menu">
          {showBackToHome && (
            <Link
              to="/"
              className="cn-nav-back"
              data-testid="nav-mobile-back-home"
              onClick={() => setOpen(false)}
            >
              <ArrowLeft size={16} strokeWidth={2.2} />
              <span>Volver a Inicio</span>
            </Link>
          )}
          {NAV_LINKS.map((l) => renderLink(l, "nav-mobile-link"))}
          <a
            href="/#contacto"
            className="cn-btn cn-btn-gradient cn-btn-sm"
            onClick={(e) => {
              navigateToHomeSection(e, "contacto");
              setOpen(false);
            }}
            data-testid="nav-mobile-cta-demo"
          >
            Solicitar Evaluación Técnica
          </a>
        </div>
      )}
    </header>
  );
};

export default Nav;
